import React, { useEffect, useState } from "react";
import { api, fmtDate, ROLE_LABEL } from "@/lib/api";
import { SectionTitle, EmptyState } from "@/components/Brutal";
import { Filter, RefreshCw, ShieldCheck, Download, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { toast } from "sonner";

const ACTION_LABEL = {
  login: "Login",
  login_failed: "Login (falhou)",
  requisition_create: "Requisição · Criada",
  requisition_approve: "Requisição · Aprovada",
  requisition_reject: "Requisição · Rejeitada",
  requisition_deliver: "Requisição · Entregue",
  requisition_delete: "Requisição · Excluída",
  requisition_approve_external: "Requisição · Aprov. Externa",
  requisition_reject_external: "Requisição · Rej. Externa",
  product_create: "Produto · Criado",
  product_delete: "Produto · Excluído",
  movement_entrada: "Movimentação · Entrada",
  movement_saida: "Movimentação · Saída",
  movement_delete: "Movimentação · Excluída",
  epi_delivery_create: "EPI · Entrega",
  epi_delivery_delete: "EPI · Excluída",
};

const actionColor = (a) => {
  if (a.includes("delete") || a.includes("failed") || a.includes("reject")) return "#E60000";
  if (a.includes("approve") || a.includes("create") || a.includes("login") || a.includes("entrada")) return "#1A7F37";
  if (a.includes("deliver") || a.includes("saida")) return "#FF4F00";
  return "#0A1F14";
};

export default function AuditLogs() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [comparative, setComparative] = useState(null);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({ action: "", resource: "", user_id: "", date_from: "", date_to: "" });

  const load = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const [logs, sum, usr, cmp] = await Promise.all([
        api.get("/audit-logs", { params }),
        api.get("/audit-logs/summary"),
        api.get("/audit-logs/users"),
        api.get("/audit-logs/comparative"),
      ]);
      setRows(logs.data);
      setSummary(sum.data);
      setUsers(usr.data);
      setComparative(cmp.data);
    } catch (e) { /* toast handled elsewhere */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const exportCsv = async () => {
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const res = await api.get("/audit-logs/export.csv", { params, responseType: "blob" });
      // Extrai filename do Content-Disposition ou usa fallback
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^"]+)"?/i);
      const fname = match ? match[1] : `auditoria_coomflona_${new Date().toISOString().slice(0,10)}.csv`;

      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exportado com sucesso");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao exportar CSV");
    }
  };

  return (
    <div data-testid="audit-page">
      <SectionTitle
        action={
          <div className="flex gap-2">
            <button onClick={exportCsv} className="brutal-btn bg-[#1A7F37] text-white px-3 py-1.5 text-xs font-display font-bold uppercase tracking-widest inline-flex items-center gap-1.5" data-testid="export-csv-btn">
              <Download className="w-3.5 h-3.5" /> Exportar CSV
            </button>
            <button onClick={load} className="brutal-btn bg-white border-2 border-[#111] px-3 py-1.5 text-xs font-display font-bold uppercase tracking-widest inline-flex items-center gap-1.5" data-testid="reload-audit">
              <RefreshCw className="w-3.5 h-3.5" /> Atualizar
            </button>
          </div>
        }
      >
        Auditoria do Sistema
      </SectionTitle>

      {summary && (
        <div className="grid md:grid-cols-2 gap-3 mb-4">
          <div className="brutal-card p-3">
            <div className="font-mono-data text-[10px] uppercase tracking-widest text-[#555] flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Total Histórico</div>
            <div className="font-display font-black text-3xl leading-none mt-1">{summary.total}</div>
            <div className="font-mono-data text-[10px] text-[#777] mt-1">Todos os registros persistidos</div>
            <div className="mt-3">
              <div className="font-mono-data text-[10px] uppercase tracking-widest text-[#555] mb-1">Top Ações · Últimos 30 dias</div>
              <div className="flex flex-wrap gap-1.5">
                {(summary.top_actions_30d || []).slice(0, 8).map((a) => (
                  <div key={a.action} className="border-2 px-1.5 py-0.5 text-[10px] font-mono-data flex items-center gap-1" style={{ borderColor: actionColor(a.action) }}>
                    <span className="text-[#333]">{ACTION_LABEL[a.action] || a.action}</span>
                    <span className="font-bold" style={{ color: actionColor(a.action) }}>{a.count}</span>
                  </div>
                ))}
                {(!summary.top_actions_30d || summary.top_actions_30d.length === 0) && <div className="text-xs text-[#777] font-mono-data">Sem atividades no período</div>}
              </div>
            </div>
          </div>

          {/* Comparativo 30d vs 30d anteriores */}
          <div className="brutal-card p-3" data-testid="comparative-panel">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="font-mono-data text-[10px] uppercase tracking-widest text-[#555] flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" /> Comparativo · 30d atual vs 30d anteriores
                </div>
                <div className="font-mono-data text-[9px] text-[#999] mt-0.5">Tendência por tipo de ação</div>
              </div>
            </div>
            {!comparative || !comparative.rows || comparative.rows.length === 0 ? (
              <div className="text-xs text-[#777] font-mono-data py-4 text-center">Sem dados suficientes</div>
            ) : (
              <div className="space-y-1.5 max-h-52 overflow-y-auto">
                {comparative.rows.slice(0, 8).map((r) => {
                  const maxVal = Math.max(...comparative.rows.map((x) => Math.max(x.current, x.previous)), 1);
                  const curW = (r.current / maxVal) * 100;
                  const prevW = (r.previous / maxVal) * 100;
                  const trending = r.delta > 0 ? "up" : (r.delta < 0 ? "down" : "flat");
                  const trendColor = trending === "up" ? "#1A7F37" : (trending === "down" ? "#E60000" : "#777");
                  const TrendIcon = trending === "up" ? TrendingUp : (trending === "down" ? TrendingDown : Minus);
                  return (
                    <div key={r.action} className="text-[11px]">
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-medium text-[#111] truncate">{ACTION_LABEL[r.action] || r.action}</div>
                        <div className="flex items-center gap-1 font-mono-data whitespace-nowrap" style={{ color: trendColor }}>
                          <TrendIcon className="w-3 h-3" />
                          <span className="font-bold">
                            {r.delta > 0 ? "+" : ""}{r.delta}
                          </span>
                          {r.pct !== null && (
                            <span className="text-[9px]">({r.pct > 0 ? "+" : ""}{r.pct}%)</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 mt-0.5">
                        <div className="flex-1 bg-[#EEE] h-1.5 relative">
                          <div className="absolute inset-y-0 left-0 bg-[#CCC]" style={{ width: `${prevW}%` }} title={`Anterior: ${r.previous}`} />
                        </div>
                        <div className="w-8 text-right font-mono-data text-[9px] text-[#777]">{r.previous}</div>
                      </div>
                      <div className="flex items-center gap-1 mt-0.5">
                        <div className="flex-1 bg-[#EEE] h-1.5 relative">
                          <div className="absolute inset-y-0 left-0" style={{ width: `${curW}%`, backgroundColor: actionColor(r.action) }} title={`Atual: ${r.current}`} />
                        </div>
                        <div className="w-8 text-right font-mono-data text-[9px] font-bold" style={{ color: actionColor(r.action) }}>{r.current}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="brutal-card p-3 mb-4 flex flex-wrap gap-2 items-end" data-testid="audit-filters">
        <div className="flex flex-col">
          <label className="font-mono-data text-[10px] uppercase tracking-widest text-[#555] mb-1">Ação</label>
          <select value={filters.action} onChange={(e) => setF("action", e.target.value)} className="border-2 border-[#111] p-1.5 text-xs bg-white w-52" data-testid="filter-action">
            <option value="">Todas</option>
            {Object.entries(ACTION_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div className="flex flex-col">
          <label className="font-mono-data text-[10px] uppercase tracking-widest text-[#555] mb-1">Usuário</label>
          <select value={filters.user_id} onChange={(e) => setF("user_id", e.target.value)} className="border-2 border-[#111] p-1.5 text-xs bg-white w-56" data-testid="filter-user">
            <option value="">Todos usuários</option>
            {users.map((u) => (
              <option key={u.user_id} value={u.user_id}>
                {u.name} ({u.count})
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col">
          <label className="font-mono-data text-[10px] uppercase tracking-widest text-[#555] mb-1">Recurso</label>
          <input value={filters.resource} onChange={(e) => setF("resource", e.target.value)} className="border-2 border-[#111] p-1.5 text-xs w-40" placeholder="ex: requisitions" />
        </div>
        <div className="flex flex-col">
          <label className="font-mono-data text-[10px] uppercase tracking-widest text-[#555] mb-1">De</label>
          <input type="date" value={filters.date_from} onChange={(e) => setF("date_from", e.target.value)} className="border-2 border-[#111] p-1.5 text-xs" />
        </div>
        <div className="flex flex-col">
          <label className="font-mono-data text-[10px] uppercase tracking-widest text-[#555] mb-1">Até</label>
          <input type="date" value={filters.date_to} onChange={(e) => setF("date_to", e.target.value)} className="border-2 border-[#111] p-1.5 text-xs" />
        </div>
        <button onClick={load} className="brutal-btn bg-[#FF4F00] text-white px-4 py-2 text-xs font-display font-bold uppercase tracking-widest inline-flex items-center gap-1.5" data-testid="apply-filters">
          <Filter className="w-3 h-3" /> Aplicar
        </button>
        <div className="ml-auto font-mono-data text-[10px] uppercase tracking-widest text-[#777] self-end">
          {rows.length} registro(s)
        </div>
      </div>

      <div className="brutal-card overflow-x-auto">
        <table className="brutal-table">
          <thead><tr>
            <th>Data / Hora</th>
            <th>Usuário</th>
            <th>Papel</th>
            <th>Ação</th>
            <th>Recurso</th>
            <th>Referência</th>
            <th>Unidade</th>
            <th>Detalhes</th>
            <th>IP</th>
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="text-center py-8 text-[#777] font-mono-data text-xs">Carregando...</td></tr>}
            {!loading && rows.length === 0 && <EmptyState message="Nenhum registro encontrado" cols={9} />}
            {!loading && rows.map((r) => (
              <tr key={r.id} data-testid={`audit-row-${r.id}`}>
                <td className="font-mono-data text-xs whitespace-nowrap">{fmtDate(r.created_at)}</td>
                <td>
                  <div className="font-medium text-sm">{r.user_name}</div>
                  {r.user_email && <div className="font-mono-data text-[10px] text-[#777]">{r.user_email}</div>}
                </td>
                <td className="text-xs">{ROLE_LABEL[r.user_role] || r.user_role || "—"}</td>
                <td>
                  <span className="px-2 py-0.5 text-white text-[10px] font-mono-data uppercase tracking-widest font-bold" style={{ backgroundColor: actionColor(r.action) }}>
                    {ACTION_LABEL[r.action] || r.action}
                  </span>
                </td>
                <td className="font-mono-data text-xs">{r.resource}</td>
                <td className="font-mono-data text-xs font-bold">{r.ref_number || "—"}</td>
                <td className="font-mono-data text-xs">{r.unidade === "km117" ? "KM117" : (r.unidade === "cupari" ? "Cuparí" : (r.unidade || "—"))}</td>
                <td className="text-xs text-[#555] max-w-xs">
                  {r.details && Object.keys(r.details).length > 0 && (
                    <div className="font-mono-data text-[10px]">
                      {Object.entries(r.details).slice(0, 3).map(([k, v]) => (
                        <div key={k}><strong>{k}:</strong> {String(v).slice(0, 80)}</div>
                      ))}
                    </div>
                  )}
                </td>
                <td className="font-mono-data text-[10px] text-[#777]">{r.ip || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
